'use client';

import { useState, useTransition } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PLANS } from '@/lib/plans';
import type { Plan } from '@/generated/prisma/enums';
import { createCheckoutSession } from '@/actions/billing/create-checkout-session';

const LOCALES = ['en', 'sr', 'si'] as const;
type Locale = (typeof LOCALES)[number];

const ORDER: Plan[] = ['FREE', 'STARTER', 'PRO'];

interface FeatureRow {
  key: string;
  label: string;
  free: boolean | string;
  starter: boolean | string;
  pro: boolean | string;
}

const FEATURE_ROWS: FeatureRow[] = [
  {
    key: 'invoices',
    label: 'Invoices per month',
    free: '3',
    starter: '20',
    pro: 'Unlimited',
  },
  {
    key: 'clients',
    label: 'Clients & products',
    free: true,
    starter: true,
    pro: true,
  },
  {
    key: 'locales',
    label: 'All languages (en/sr/si)',
    free: true,
    starter: true,
    pro: true,
  },
  {
    key: 'recurring',
    label: 'Recurring invoices',
    free: false,
    starter: true,
    pro: true,
  },
  {
    key: 'expenses',
    label: 'Expense tracking',
    free: false,
    starter: false,
    pro: true,
  },
  {
    key: 'ocr',
    label: 'AI receipt scanning',
    free: false,
    starter: false,
    pro: '50/mo',
  },
  {
    key: 'accountant',
    label: 'Accountant access',
    free: false,
    starter: false,
    pro: true,
  },
  {
    key: 'branding',
    label: 'No "Made with" footer',
    free: false,
    starter: true,
    pro: true,
  },
];

export function PricingCards({
  isAuthenticated,
  currentPlan,
  isGrandfathered = false,
}: {
  isAuthenticated: boolean;
  currentPlan: Plan | null;
  isGrandfathered?: boolean;
}) {
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const locale = (pathname.split('/')[1] || 'en') as Locale;

  const handleCta = (plan: Plan) => {
    setError(null);
    if (plan === 'FREE') {
      router.push(`/${locale}/register`);
      return;
    }
    if (!isAuthenticated) {
      router.push(
        `/${locale}/register?intent=upgrade&plan=${plan}&interval=${interval}`
      );
      return;
    }
    setPendingPlan(plan);
    startTransition(async () => {
      const res = await createCheckoutSession({
        plan: plan as 'STARTER' | 'PRO',
        interval,
      });
      if (res.url) {
        window.location.href = res.url;
      } else {
        setError(res.error ?? 'Failed to start checkout');
        setPendingPlan(null);
      }
    });
  };

  return (
    <div className="py-16 px-5 bg-card min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="text-center space-y-4 mb-12">
          <span className="text-sm font-semibold tracking-wide uppercase text-emerald-600 dark:text-emerald-400">
            Simple Pricing
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            Start free, upgrade when you grow
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three plans that scale with your business. Cancel anytime, no
            questions asked.
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-lg border bg-background p-1">
            <button
              onClick={() => setInterval('month')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                interval === 'month'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval('year')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                interval === 'year'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">
                Save 22%
              </span>
            </button>
          </div>
        </div>

        {isGrandfathered && (
          <div className="max-w-md mx-auto mb-6 px-4 py-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200 text-sm text-center">
            You&apos;re on a legacy lifetime plan with all Pro features
            unlocked. No upgrade needed.
          </div>
        )}

        {error && (
          <div className="max-w-md mx-auto mb-6 px-4 py-3 rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {ORDER.map((plan) => {
            const def = PLANS[plan];
            const price =
              interval === 'month' ? def.priceMonthly : def.priceYearly;
            const isCurrent = currentPlan === plan;
            const isPro = plan === 'PRO';
            return (
              <div
                key={plan}
                className={`rounded-2xl border bg-background p-6 flex flex-col ${
                  isPro
                    ? 'border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                    : ''
                }`}
              >
                {isPro && (
                  <span className="self-start mb-3 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    Most popular
                  </span>
                )}
                <h3 className="text-2xl font-bold mb-1">{def.label}</h3>
                <div className="my-4">
                  <span className="text-4xl font-bold">€{price}</span>
                  {price > 0 && (
                    <span className="text-muted-foreground ml-1">
                      /{interval === 'month' ? 'mo' : 'yr'}
                    </span>
                  )}
                  {price > 0 && locale === 'sr' && (
                    <div className="text-xs text-muted-foreground mt-1">
                      ~{Math.round(price * 117).toLocaleString('sr-RS')} RSD /
                      {interval === 'month' ? 'mes' : 'god'}
                    </div>
                  )}
                </div>
                <ul className="space-y-3 text-sm mb-8 flex-1">
                  {FEATURE_ROWS.map((row) => {
                    const value =
                      plan === 'FREE'
                        ? row.free
                        : plan === 'STARTER'
                          ? row.starter
                          : row.pro;
                    return (
                      <li key={row.key} className="flex items-start gap-2">
                        {typeof value === 'string' ? (
                          <Check className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        ) : value ? (
                          <Check className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        ) : (
                          <X className="w-4 h-4 mt-0.5 text-muted-foreground/50 shrink-0" />
                        )}
                        <span
                          className={
                            value === false ? 'text-muted-foreground/60' : ''
                          }
                        >
                          {row.label}
                          {typeof value === 'string' && (
                            <span className="font-semibold ml-1">
                              ({value})
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <Button
                  onClick={() => handleCta(plan)}
                  disabled={
                    isCurrent || pendingPlan !== null || isGrandfathered
                  }
                  variant={isPro ? 'default' : 'outline'}
                  size="lg"
                  className="w-full"
                >
                  {isGrandfathered
                    ? 'Lifetime plan'
                    : isCurrent
                      ? 'Current plan'
                      : pendingPlan === plan
                        ? 'Loading…'
                        : plan === 'FREE'
                          ? 'Get started'
                          : 'Upgrade'}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          All prices in EUR. VAT may apply depending on your country. Cancel
          anytime via the customer portal.
        </p>
      </div>
    </div>
  );
}
